import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Share2, Percent, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import Affiliates from "../Affiliates";
import { SectionHeader } from "./Capture";

function Commissions() {
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [c, p] = await Promise.all([api.get("/amplify/commissions"), api.get("/products")]);
      setRows(c.data || []);
      setProducts(p.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const byProduct = (pid) => rows.find(r => (r.product_id || null) === (pid || null));

  const save = async (productId, percent) => {
    setSaving(productId || "__global__");
    try {
      await api.put("/amplify/commissions", { product_id: productId || null, percent: Number(percent) });
      toast.success("Commission saved");
      await load();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <div className="p-10 inline-flex items-center gap-2 text-meta"><Loader2 className="h-4 w-4 animate-spin" /> Loading commissions…</div>;

  const globalRow = byProduct(null) || { percent: 30, label: "Global default" };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1100px] mx-auto space-y-6" data-testid="amplify-commissions">
      <CommissionRow key="__global__" label="Global default" hint="Applies to any product without an override"
        initial={globalRow.percent} saving={saving === "__global__"}
        onSave={(v) => save(null, v)} />

      <div className="surface p-5">
        <div className="flex items-center gap-2 mb-4">
          <Percent className="h-4 w-4 text-ai" />
          <h3 className="font-display font-bold text-ink">Per-product overrides</h3>
        </div>
        {products.length === 0 ? (
          <p className="text-sm text-meta">Add products in Experiences → Products to set custom commissions.</p>
        ) : (
          <div className="space-y-3">
            {products.map(p => {
              const r = byProduct(p.id);
              return (
                <CommissionRow key={p.id} label={p.name} hint={`Product · $${p.price || 0}`}
                  initial={r?.percent ?? globalRow.percent} saving={saving === p.id}
                  onSave={(v) => save(p.id, v)} />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function CommissionRow({ label, hint, initial, saving, onSave }) {
  const [v, setV] = useState(String(initial ?? 30));
  return (
    <div className="surface p-4 flex items-center gap-3 flex-wrap">
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-ink text-sm">{label}</p>
        <p className="text-xs text-meta">{hint}</p>
      </div>
      <div className="inline-flex items-center gap-1 rounded-xl border border-line bg-zinc-50 px-2 py-1">
        <input type="number" min="0" max="100" step="0.5" value={v} onChange={(e) => setV(e.target.value)}
          className="w-16 bg-transparent text-right text-sm font-semibold text-ink outline-none" data-testid={`commission-input-${label}`} />
        <span className="text-meta text-xs">%</span>
      </div>
      <button onClick={() => onSave(v)} disabled={saving} data-testid={`commission-save-${label}`}
        className="rounded-full ashra-gradient-bg px-4 py-2 text-xs font-semibold text-white inline-flex items-center gap-1 disabled:opacity-60 focus-ring">
        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
      </button>
    </div>
  );
}

const TABS = [
  { id: "affiliates",  label: "Affiliates",  icon: Share2,  comp: Affiliates,  hint: "Ambassadors driving revenue" },
  { id: "commissions", label: "Commissions", icon: Percent, comp: Commissions, hint: "Editable per-product economics" },
];

export default function Amplify() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("tab") || "affiliates";
  const [tab, setTab] = useState(initial);
  const Active = TABS.find(t => t.id === tab)?.comp || Affiliates;
  const meta = TABS.find(t => t.id === tab);

  return (
    <div data-testid="amplify-page">
      <SectionHeader title="Amplify" subtitle="Let others sell for you — and reward them transparently" tabs={TABS} active={tab}
        onChange={(id) => { setTab(id); setParams({ tab: id }); }} hint={meta?.hint} />
      <Active />
    </div>
  );
}
